export interface Vec {
  x: number;
  y: number;
}

export function vec(x = 0, y = 0): Vec {
  return { x, y };
}

export function clone(v: Vec): Vec {
  return { x: v.x, y: v.y };
}

export function add(a: Vec, b: Vec): Vec {
  return { x: a.x + b.x, y: a.y + b.y };
}

export function sub(a: Vec, b: Vec): Vec {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function scale(a: Vec, s: number): Vec {
  return { x: a.x * s, y: a.y * s };
}

export function len(v: Vec): number {
  return Math.hypot(v.x, v.y);
}

export function len2(v: Vec): number {
  return v.x * v.x + v.y * v.y;
}

export function dist(a: Vec, b: Vec): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function dist2(a: Vec, b: Vec): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

export function norm(v: Vec): Vec {
  const l = Math.hypot(v.x, v.y);
  if (l < 1e-8) return { x: 0, y: 0 };
  return { x: v.x / l, y: v.y / l };
}

export function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

export function clampVec(v: Vec, minX: number, minY: number, maxX: number, maxY: number): Vec {
  return { x: clamp(v.x, minX, maxX), y: clamp(v.y, minY, maxY) };
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function ang(v: Vec): number {
  return Math.atan2(v.y, v.x);
}

export function fromAngle(a: number, s = 1): Vec {
  return { x: Math.cos(a) * s, y: Math.sin(a) * s };
}

export function rotate(v: Vec, a: number): Vec {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: v.x * c - v.y * s, y: v.x * s + v.y * c };
}

export function dot(a: Vec, b: Vec): number {
  return a.x * b.x + a.y * b.y;
}

export function closestOnAabb(px: number, py: number, x: number, y: number, w: number, h: number): Vec {
  return {
    x: clamp(px, x, x + w),
    y: clamp(py, y, y + h),
  };
}

export function circleHitsAabb(
  cx: number,
  cy: number,
  r: number,
  x: number,
  y: number,
  w: number,
  h: number,
): boolean {
  const p = closestOnAabb(cx, cy, x, y, w, h);
  return dist2({ x: cx, y: cy }, p) < r * r;
}

export function segmentHitsAabb(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  x: number,
  y: number,
  w: number,
  h: number,
): boolean {
  const minX = Math.min(ax, bx);
  const maxX = Math.max(ax, bx);
  const minY = Math.min(ay, by);
  const maxY = Math.max(ay, by);
  if (maxX < x || minX > x + w || maxY < y || minY > y + h) return false;
  if (ax >= x && ax <= x + w && ay >= y && ay <= y + h) return true;
  if (bx >= x && bx <= x + w && by >= y && by <= y + h) return true;
  return (
    lineHitsSeg(ax, ay, bx, by, x, y, x + w, y) ||
    lineHitsSeg(ax, ay, bx, by, x + w, y, x + w, y + h) ||
    lineHitsSeg(ax, ay, bx, by, x + w, y + h, x, y + h) ||
    lineHitsSeg(ax, ay, bx, by, x, y + h, x, y)
  );
}

function lineHitsSeg(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
): boolean {
  const den = (ax - bx) * (cy - dy) - (ay - by) * (cx - dx);
  if (Math.abs(den) < 1e-9) return false;
  const t = ((ax - cx) * (cy - dy) - (ay - cy) * (cx - dx)) / den;
  const u = -((ax - bx) * (ay - cy) - (ay - by) * (ax - cx)) / den;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}

export function angleDelta(from: number, to: number): number {
  let d = to - from;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}
